import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

import { SiteVisitor } from "~/api/Api";
import Card from "~/components/ui/card/Card";
import DataTable, {
  DataTableColumnMeta,
} from "~/components/ui/dataTable/DataTable";

import { formatChartValue } from "./BreakdownChart";
import Pill from "./Pill";
import styles from "./VisitorDetailsTable.module.scss";

type VisitorDetailsTableProps = {
  data: SiteVisitor[];
  isLoading: boolean;
};

type TypeFilter = "human" | "bot" | "se" | "llm";

const TYPE_FILTERS: { key: TypeFilter; label: string }[] = [
  { key: "human", label: "Human" },
  { key: "bot", label: "Bots" },
  { key: "se", label: "Search Engines" },
  { key: "llm", label: "LLM" },
];

const columns: ColumnDef<SiteVisitor>[] = [
  {
    accessorKey: "family",
    header: "Family",
    cell: (info) => (info.getValue() as string) || "—",
  },
  {
    accessorKey: "name",
    header: "Name",
    cell: (info) => (info.getValue() as string) || "—",
  },
  {
    accessorKey: "platform",
    header: "Platform",
    cell: (info) => (info.getValue() as string) || "—",
  },
  {
    id: "type",
    header: "Type",
    size: 120,
    enableSorting: false,
    cell: ({ row }) => (
      <VisitorTypeBadges bot={row.original.bot} kind={row.original.kind} />
    ),
  },
  {
    accessorKey: "total",
    header: "Visits",
    size: 100,
    meta: { align: "right" } satisfies DataTableColumnMeta,
    cell: (info) => (
      <span className={styles.totalValue}>
        {formatChartValue(info.getValue() as number)}
      </span>
    ),
  },
];

const VisitorDetailsTable = ({ data, isLoading }: VisitorDetailsTableProps) => {
  const [activeFilters, setActiveFilters] = useState<Set<TypeFilter>>(
    new Set(),
  );
  const [search, setSearch] = useState("");

  const availableFilters = useMemo(
    () =>
      TYPE_FILTERS.filter(({ key }) =>
        data.some((row) => {
          if (key === "human") return !row.bot;
          if (key === "se") return row.bot && row.kind === "se";
          if (key === "llm") return row.bot && row.kind === "llm";
          if (key === "bot") return row.bot && row.kind === "unknown";

          return false;
        }),
      ),
    [data],
  );

  const toggleFilter = useCallback((key: TypeFilter) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);

      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }

      return next;
    });
  }, []);

  const filteredData = useMemo(() => {
    let result = data;

    if (activeFilters.size > 0) {
      result = result.filter((row) => {
        if (activeFilters.has("human") && !row.bot) return true;
        if (activeFilters.has("se") && row.bot && row.kind === "se")
          return true;
        if (activeFilters.has("llm") && row.bot && row.kind === "llm")
          return true;
        if (activeFilters.has("bot") && row.bot && row.kind === "unknown")
          return true;

        return false;
      });
    }

    const q = search.trim().toLowerCase();

    if (q) {
      result = result.filter(
        (row) =>
          row.family?.toLowerCase().includes(q) ||
          row.name?.toLowerCase().includes(q) ||
          row.platform?.toLowerCase().includes(q),
      );
    }

    return result;
  }, [data, activeFilters, search]);

  return (
    <div className={styles.root}>
      <Card title="Visitor Breakdown">
        <DataTable
          columns={columns}
          data={filteredData}
          isLoading={isLoading}
          skeletonRows={8}
          emptyText="No visitor data available for this period."
          pageSize={20}
          initialSorting={[{ id: "total", desc: true }]}
          search={{
            value: search,
            onChange: setSearch,
            placeholder: "Search by family, name, platform…",
          }}
          toolbarActions={
            availableFilters.length > 0 ? (
              <div className={styles.filterChips}>
                {availableFilters.map(({ key, label }) => (
                  <Pill
                    key={key}
                    active={activeFilters.has(key)}
                    onClick={() => toggleFilter(key)}
                  >
                    {label}
                  </Pill>
                ))}
              </div>
            ) : undefined
          }
        />
      </Card>
    </div>
  );
};

type VisitorTypeBadgesProps = { bot: boolean; kind: SiteVisitor["kind"] };

const VisitorTypeBadges = ({ bot, kind }: VisitorTypeBadgesProps) => {
  if (!bot) return <span className={styles.badgeHuman}>Human</span>;
  if (kind === "se") return <span className={styles.badgeSe}>SE</span>;
  if (kind === "llm") return <span className={styles.badgeLlm}>LLM</span>;

  return <span className={styles.badgeBot}>Bot</span>;
};

export default VisitorDetailsTable;
